"use client"

import { liveLocales } from "@/i18n/apps"
import { useAppContext } from "@/src/app/contexts/AppProvider"
import {
    buildLocaleHref,
    extractResolvedPathAfterLocale,
    hrefPathname
} from "@/src/utils/countryPicker/localeHrefs"
import { usePathname } from "next/navigation"
import { useMemo } from "react"

const LOCALE_SEGMENTS = liveLocales.map(locale => locale.replace(/^\/+/, ""))

export type CountryPickerHrefs = {
    /** Locale segment (`en-se`) → the link the picker shows for it. */
    hrefByLocale: Record<string, string>
    /** The distinct pathnames behind those links, in the form the resolve route takes. */
    pathnames: string[]
}

/**
 * One derivation shared by the picker trigger and the picker panel, so the pathnames
 * the trigger preloads on hover are exactly the ones the panel asks for on open.
 */
export const useCountryPickerHrefs = (): CountryPickerHrefs => {
    const pathname = usePathname()
    const { hrefLangs } = useAppContext()
    // `hrefLangs` is a new object on every navigation; its values are what matters.
    const hrefLangsKey = hrefLangs ? Object.values(hrefLangs).join("|") : ""

    return useMemo(
        () => {
            const pathAfterLocale = extractResolvedPathAfterLocale(pathname)
            const hrefByLocale: Record<string, string> = {}
            for (const segment of LOCALE_SEGMENTS) {
                hrefByLocale[segment] = buildLocaleHref(
                    segment,
                    hrefLangs,
                    pathAfterLocale
                )
            }
            const pathnames = [...new Set(Object.values(hrefByLocale).map(hrefPathname))]

            return { hrefByLocale, pathnames }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pathname, hrefLangsKey]
    )
}
